import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";

// Auto-recover from stale dynamic-import chunks after a deploy.
const isChunkLoadError = (msg: string) =>
  /Failed to fetch dynamically imported module/i.test(msg) ||
  /Importing a module script failed/i.test(msg) ||
  /error loading dynamically imported module/i.test(msg) ||
  /ChunkLoadError/i.test(msg) ||
  /Loading chunk [\d]+ failed/i.test(msg);

const tryReload = () => {
  const KEY = "__chunk_reload_at";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  const now = Date.now();
  if (now - last > 10000) {
    sessionStorage.setItem(KEY, String(now));
    window.location.reload();
  }
};

window.addEventListener("error", (e) => {
  if (isChunkLoadError(e?.message || "")) tryReload();
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = (e?.reason && (e.reason.message || String(e.reason))) || "";
  if (isChunkLoadError(msg)) tryReload();
});

// ── Auto image lazy-loading & async decoding (cross-browser perf win) ───────
// Adds loading="lazy" + decoding="async" to any <img> that doesn't already
// declare them. Skips eager hero images (data-eager) and tiny pixels.
const enhanceImg = (img: HTMLImageElement) => {
  if (img.dataset.lazyApplied === "1") return;
  img.dataset.lazyApplied = "1";
  if (img.hasAttribute("data-eager")) return;
  if (!img.getAttribute("loading")) img.setAttribute("loading", "lazy");
  if (!img.getAttribute("decoding")) img.setAttribute("decoding", "async");
};
if (typeof window !== "undefined" && "MutationObserver" in window) {
  const scan = (root: ParentNode) => {
    root.querySelectorAll?.("img").forEach((el) => enhanceImg(el as HTMLImageElement));
  };
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      m.addedNodes.forEach((n) => {
        if (n.nodeType !== 1) return;
        const el = n as Element;
        if (el.tagName === "IMG") enhanceImg(el as HTMLImageElement);
        else scan(el);
      });
    }
  });
  document.addEventListener("DOMContentLoaded", () => {
    scan(document.body);
    mo.observe(document.body, { childList: true, subtree: true });
  });
}

// ── Service worker: detect new build & prompt one-time refresh ─────────────
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      // Check for updates every 5 min while tab is open
      setInterval(() => reg.update().catch(() => {}), 5 * 60 * 1000);
      reg.addEventListener("updatefound", () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener("statechange", () => {
          if (sw.state === "installed" && navigator.serviceWorker.controller) {
            // New version available — silently refresh once per session
            const KEY = "__sw_refreshed_at";
            const last = Number(sessionStorage.getItem(KEY) || 0);
            if (Date.now() - last > 60_000) {
              sessionStorage.setItem(KEY, String(Date.now()));
              window.location.reload();
            }
          }
        });
      });
    }).catch(() => {});
  });
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
